/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { IonContent, IonLabel, IonList } from '@ionic/react';
import { TAKEOUT_TYPES } from '../../../../global/request/bet';
import { getMenu } from '../../../../global/request/restaurant';
import { cache as menuCache } from '../../../../global/useMenu';
import { AuthContext } from '../../../authentication/AuthContext';
import OverLimitText from '../OverLimitText';
import TakerItemCard from '../TakerItemCard';
import TakerModalFooter from '../TakerModalFooter';
import TakerModalHeader from '../TakerModalHeader';
import '../../../../styles/fight/takerProcess/ModifyMenu.scss';

const ModifyMenu = ({ fightData, optionalTakerVenue, startingSelections, onNext, onCloseModal }) => {
  const authCtx = useContext(AuthContext);
  const bearerToken = authCtx.tokens.idToken;

  const makerTotalPrice = useMemo(
    () => fightData.restaurantItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [fightData],
  );

  // If we want to support price matching for in-person, remove the check for takeoutType
  const takerVenue = useMemo(() => {
    if (fightData.takeoutType === TAKEOUT_TYPES.DELIVERY && optionalTakerVenue) {
      return optionalTakerVenue;
    }

    return {
      id: fightData.restaurantId,
      name: fightData.restaurantName,
      takeoutType: fightData.takeoutType,
    };
  }, [optionalTakerVenue, fightData]);

  const [menu, setMenu] = useState({});

  const modifiedSelectionsRef = useRef({});
  const [modifiedTotal, setModifiedTotal] = useState(0);

  const getMenuFromItems = useCallback(
    (referenceSelections, menuItems) => {
      const groupedMenu = {};
      const selectedItems = {};
      let totalPrice = 0;

      menuItems.forEach(item => {
        if (item.price > makerTotalPrice) {
          console.warn(`Skipping item [${item.name}] - price above makerTotalPrice`);
          return;
        }

        const referenceAmount = referenceSelections[item.id];

        const itemInfo = {
          ...item,
          formattedPrice: `${(item.price / 100).toFixed(2)}`,
          amount: referenceAmount || 0,
        };

        const { category } = item;
        if (groupedMenu[category]) {
          groupedMenu[category].push(itemInfo);
        } else {
          groupedMenu[category] = [itemInfo];
        }

        if (referenceAmount) {
          selectedItems[item.id] = referenceAmount;
          totalPrice += item.price * referenceAmount;
        }
      });

      return { groupedMenu, selectedItems, totalPrice };
    },
    [makerTotalPrice],
  );

  const changeItemAmount = (itemId, itemPrice, itemAmount) => {
    const oldQuantity = modifiedSelectionsRef.current[itemId] || 0;
    const oldPriceContribution = oldQuantity * itemPrice;
    const newPriceContribution = itemAmount * itemPrice;
    const priceChange = newPriceContribution - oldPriceContribution;
    setModifiedTotal(prev => prev + priceChange);

    modifiedSelectionsRef.current[itemId] = itemAmount;
  };

  const fetchMenu = useCallback(
    async referenceSelections => {
      try {
        const isTakerDelivery = takerVenue.takeoutType === TAKEOUT_TYPES.DELIVERY;
        const cacheKey = `${takerVenue.id}_${isTakerDelivery}`;

        const isCacheMiss = !menuCache.has(cacheKey);
        let menuResult;

        if (isCacheMiss) {
          menuResult = await getMenu(takerVenue.id, bearerToken, isTakerDelivery);
        } else {
          menuResult = menuCache.retrieve(cacheKey).menu;
        }

        const { groupedMenu, selectedItems, totalPrice } = getMenuFromItems(referenceSelections, menuResult);

        setMenu(groupedMenu);
        modifiedSelectionsRef.current = selectedItems;
        setModifiedTotal(totalPrice);

        if (isCacheMiss) {
          const cacheEntry = {
            menu: menuResult,
            categories: Object.keys(groupedMenu),
            menuSource: 'standard',
          };
          menuCache.store(cacheKey, cacheEntry);
        }
      } catch (error) {
        console.error('[ModifyMenu] Failed to fetch menu:', error);
      }
    },
    [takerVenue, bearerToken, getMenuFromItems],
  );

  const saveChanges = () => {
    const formattedItems = [];
    Object.values(menu).forEach(items =>
      items.forEach(item => {
        const amount = modifiedSelectionsRef.current[item.id] || 0;
        if (amount === 0) return;

        formattedItems.push({
          id: item.id,
          name: item.name,
          price: item.price,
          amount,
        });
      }),
    );

    onNext(formattedItems, modifiedTotal);
  };

  useEffect(() => {
    const referenceSelections = {};

    if (startingSelections) {
      startingSelections.forEach(item => {
        referenceSelections[item.id] = item.amount;
      });
    } else {
      fightData.restaurantItems.forEach(item => {
        const itemId = item.menu_item_id === -1 ? item.product_id : item.menu_item_id;
        referenceSelections[itemId] = item.quantity;
      });
    }

    fetchMenu(referenceSelections);
  }, [startingSelections, fightData, takerVenue, fetchMenu]);

  return (
    <>
      <TakerModalHeader pageName='Modify Items' onXButton={onCloseModal} />

      <IonContent className='modify-menu-content'>
        <h3 className='heading'>
          PICK NEW ITEM <br />${(makerTotalPrice / 100).toFixed(2)} OR BELOW
        </h3>

        <IonList className='menu-list'>
          {Object.entries(menu).map(([categoryName, items]) => (
            <div className='menu-section' key={categoryName}>
              <IonLabel className='section-label'>{categoryName}</IonLabel>
              {items
                .filter(item => (fightData.takeoutType === TAKEOUT_TYPES.DELIVERY ? item.price > 0 : true)) // temp fix before customization page for $0 items
                .map(item => (
                  <TakerItemCard
                    item={item}
                    onAmountChange={amount => changeItemAmount(item.id, item.price, amount)}
                    key={item.id}
                  />
                ))}
            </div>
          ))}
        </IonList>
      </IonContent>

      <TakerModalFooter
        buttonName='Next'
        isDisabled={modifiedTotal > makerTotalPrice}
        onClick={saveChanges}
        ComponentAboveButton={() => OverLimitText(modifiedTotal, makerTotalPrice)}
      />
    </>
  );
};

export default ModifyMenu;
